import { apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';

import type { MockContratoAtivo, MockEligibleTenant } from '../lib/mockData';
import type {
  ConverterTrialFormData,
  EncerrarTrialFormData,
  EstenderTrialFormData,
  IniciarTrialFormData,
  TrialAction,
  TrialHistoryEntry,
  TrialStatus,
  TrialTenant,
} from '../types';

interface ApiContractEvent {
  id: string;
  type: TrialAction;
  contractId: string;
  rootContractId: string;
  createdAt: string;
  createdByUserId: string | null;
  createdByUserName: string | null;
  metadata: Record<string, unknown> | null;
}

interface ApiContract {
  id: string;
  workspaceId: string;
  productId: string;
  parentId: string | null;
  product?: { name?: string; category?: string | null } | null;
  escritorio?: { id: string; nomeFantasia: string; razaoSocial?: string | null } | null;
  status: string;
  customValue: string | number | null;
  discountPercentage: string | number | null;
  dueDay: number;
  paymentMethod: 'PIX' | 'BOLETO' | 'CARD';
  expirationAlertDays: number | null;
  variationId: string | null;
  postalBilling: 'SEPARATE' | 'CORRELATED' | null;
  quantity: number | null;
  trialPeriodDays: number | null;
  trialStart: string | null;
  trialEnd: string | null;
  suspensionReason: string | null;
  terminationReason: string | null;
  editedByUserName: string | null;
  createdAt: string;
  updatedAt: string;
  startAt: string;
  endAt: string | null;
  events?: ApiContractEvent[];
}

interface ApiEscritorio {
  id: string;
  nome?: string;
  nomeFantasia?: string;
  razaoSocial?: string | null;
}

const toLocalIso = (dateOnly: string | null | undefined): string | null => {
  if (!dateOnly) return null;
  if (dateOnly.includes('T')) return dateOnly;
  return new Date(`${dateOnly}T00:00:00`).toISOString();
};

const computeTrialPeriodDays = (dataInicio: string, dataExpiracao: string): number => {
  const start = new Date(dataInicio.includes('T') ? dataInicio : `${dataInicio}T00:00:00`);
  const end = new Date(dataExpiracao.includes('T') ? dataExpiracao : `${dataExpiracao}T00:00:00`);
  return Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
};

const projectTrialStatus = (api: ApiContract): TrialStatus => {
  if (api.status === 'CANCELED' || api.status === 'EXPIRED') return 'ENCERRADO';
  if (api.status === 'TRIAL') {
    const expirado = api.trialEnd != null && new Date(api.trialEnd).getTime() <= Date.now();
    return expirado ? 'EXPIRADO' : 'ATIVO';
  }
  return 'CONVERTIDO';
};

const projectEvent = (ev: ApiContractEvent): TrialHistoryEntry => {
  const meta = ev.metadata ?? {};
  const motivo = typeof meta.reason === 'string' ? meta.reason : null;
  const dataAnterior = typeof meta.previousTrialEnd === 'string' ? meta.previousTrialEnd : null;
  const dataNova =
    typeof meta.newTrialEnd === 'string'
      ? meta.newTrialEnd
      : typeof meta.trialEnd === 'string'
        ? meta.trialEnd
        : null;
  return {
    id: ev.id,
    acao: ev.type,
    usuarioId: ev.createdByUserId,
    usuarioNome: ev.createdByUserName,
    dataHora: ev.createdAt,
    motivo,
    dataExpiracaoAnterior: dataAnterior,
    dataExpiracaoNova: dataNova,
    contratosCriadosIds: null,
  };
};

const projectContractToTrial = (api: ApiContract): TrialTenant => {
  const dataInicio = api.trialStart ?? api.startAt;
  const dataExpiracao = api.trialEnd ?? api.createdAt;
  const historico: TrialHistoryEntry[] =
    api.events && api.events.length > 0
      ? api.events.map(projectEvent)
      : [
          {
            id: `h_${api.id}_create`,
            acao: 'CRIACAO',
            usuarioId: null,
            usuarioNome: api.editedByUserName,
            dataHora: dataInicio,
            motivo: null,
            dataExpiracaoAnterior: null,
            dataExpiracaoNova: dataExpiracao,
            contratosCriadosIds: null,
          },
        ];

  return {
    id: api.id,
    rootContractId: api.parentId ?? api.id,
    tenantId: api.workspaceId,
    tenantNome: api.escritorio?.nomeFantasia ?? api.escritorio?.razaoSocial ?? '',
    tenantEmail: null,
    produtoId: api.productId,
    produtoNome: api.product?.name ?? '—',
    produtoTrialDays: api.trialPeriodDays,
    produtoTrialBehavior: null,
    status: projectTrialStatus(api),
    dataInicio,
    dataExpiracao,
    alertaVencimentoDias: api.expirationAlertDays,
    observacao: null,
    motivoEncerramento: api.terminationReason ?? api.suspensionReason ?? null,
    criadoPor: api.editedByUserName,
    criadoEm: api.createdAt,
    atualizadoEm: api.updatedAt,
    historico,
  };
};

const mapPaymentToApi = (method: 'PIX' | 'BOLETO' | 'CARTAO'): 'PIX' | 'BOLETO' | 'CARD' =>
  method === 'CARTAO' ? 'CARD' : method;

const handleResponse = async <T>(response: Response, errorMsg: string): Promise<T> => {
  const json = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(json?.error?.message ?? errorMsg);
  }
  return json.data as T;
};

export const trialsService = {
  async fetchAll(): Promise<TrialTenant[]> {
    const response = await apiGet('/products/contracts/trials');
    const contracts = await handleResponse<ApiContract[]>(response, 'Falha ao carregar trials');
    return contracts
      .map(projectContractToTrial)
      .sort((a, b) => new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime());
  },

  async fetchByTenantId(tenantId: string): Promise<TrialTenant[]> {
    const response = await apiGet('/products/contracts', { workspaceId: tenantId });
    const contracts = await handleResponse<ApiContract[]>(
      response,
      'Falha ao carregar histórico de trial',
    );
    return contracts
      .filter((c) => c.trialPeriodDays != null && c.trialPeriodDays > 0)
      .map(projectContractToTrial)
      .sort((a, b) => new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime());
  },

  async fetchEligibleTenants(params?: {
    search?: string;
    limit?: number;
  }): Promise<MockEligibleTenant[]> {
    const query: Record<string, string | number> = { limit: params?.limit ?? 30 };
    const search = params?.search?.trim();
    if (search) {
      query.search = search;
    }
    const [escritoriosRes, trialsRes] = await Promise.all([
      apiGet('/escritorios', query),
      apiGet('/products/contracts/trials'),
    ]);
    const escritoriosPayload = await handleResponse<
      ApiEscritorio[] | { data?: ApiEscritorio[]; items?: ApiEscritorio[] }
    >(escritoriosRes, 'Falha ao listar escritórios');
    const escritorios = Array.isArray(escritoriosPayload)
      ? escritoriosPayload
      : (escritoriosPayload.data ?? escritoriosPayload.items ?? []);
    const trials = await handleResponse<ApiContract[]>(trialsRes, 'Falha ao listar trials');
    const contratosAtivosPorTenant = new Map<string, MockContratoAtivo[]>();
    for (const contract of trials) {
      if (contract.status === 'CANCELED' || contract.status === 'EXPIRED') continue;
      const tipo: MockContratoAtivo['tipo'] = contract.status === 'TRIAL' ? 'TRIAL' : 'CONTRATO';
      const lista = contratosAtivosPorTenant.get(contract.workspaceId) ?? [];
      lista.push({
        produtoId: contract.productId,
        categoria: contract.product?.category ?? null,
        tipo,
      });
      contratosAtivosPorTenant.set(contract.workspaceId, lista);
    }

    return escritorios.map((esc) => {
      const contratosAtivos = contratosAtivosPorTenant.get(esc.id) ?? [];
      return {
        id: esc.id,
        nome: esc.nome ?? esc.nomeFantasia ?? esc.razaoSocial ?? esc.id,
        email: null as unknown as string,
        emTrialAtivo: contratosAtivos.some((c) => c.tipo === 'TRIAL'),
        contratosAtivos,
        teveTrialEncerradoSemConversao: false,
      };
    });
  },

  async iniciarTrial(data: IniciarTrialFormData): Promise<TrialTenant> {
    const trialPeriodDays = computeTrialPeriodDays(data.dataInicio, data.dataExpiracao);
    const payload = {
      workspaceId: data.tenantId,
      productId: data.produtoId,
      paymentMethod: mapPaymentToApi(data.metodoPagamento),
      dueDay: data.diaVencimento,
      expirationAlertDays: data.alertaVencimentoDias,
      trialPeriodDays,
      startAt: toLocalIso(data.dataInicio),
    };
    const response = await apiPost('/products/contracts', payload);
    const contract = await handleResponse<ApiContract>(response, 'Falha ao iniciar trial');
    return projectContractToTrial(contract);
  },

  async estenderTrial(data: EstenderTrialFormData): Promise<TrialTenant> {
    const currentRes = await apiGet(`/products/contracts/${data.trialId}`);
    const current = await handleResponse<ApiContract>(currentRes, 'Trial não encontrado');

    const currentTrialDays = current.trialPeriodDays ?? 0;
    if (!current.trialStart) {
      throw new Error('Contrato sem trial ativo para estender');
    }
    const newTotal = computeTrialPeriodDays(current.trialStart, data.novaDataExpiracao);
    if (newTotal <= currentTrialDays) {
      throw new Error('A nova data deve ser posterior à data de expiração atual');
    }

    const payload = {
      customValue: current.customValue != null ? Number(current.customValue) : null,
      discountPercentage:
        current.discountPercentage != null ? Number(current.discountPercentage) : null,
      dueDay: current.dueDay,
      paymentMethod: current.paymentMethod,
      expirationAlertDays: current.expirationAlertDays,
      variationId: current.variationId,
      postalBilling: current.postalBilling,
      quantity: current.quantity,
      trialPeriodDays: newTotal,
      startAt: current.startAt,
      endAt: current.endAt,
    };
    const response = await apiPut(`/products/contracts/${data.trialId}`, payload);
    const updated = await handleResponse<ApiContract>(response, 'Falha ao estender trial');
    return projectContractToTrial(updated);
  },

  async converterTrial(data: ConverterTrialFormData): Promise<TrialTenant> {
    const response = await apiPatch(`/products/contracts/${data.trialId}/convert-trial`, {});
    const converted = await handleResponse<ApiContract>(response, 'Falha ao converter trial');
    return projectContractToTrial(converted);
  },

  async encerrarTrial(data: EncerrarTrialFormData): Promise<TrialTenant> {
    if (!data.cienteIrreversivel) {
      throw new Error('Confirmação de irreversibilidade obrigatória');
    }
    const response = await apiPatch(`/products/contracts/${data.trialId}/terminate`, {
      reason: data.motivo,
    });
    const terminated = await handleResponse<ApiContract>(response, 'Falha ao encerrar trial');
    return projectContractToTrial(terminated);
  },
};
